import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { PRODUCT_DIFFICULTY_LEVELS } from 'bundles/search-common/constants';
import type { ProductCardSpecializationFragmentFragment } from 'bundles/unified-description-page-common/queries/__generated__/ProductCardSpecializationFragment';

const DIFFICULTY_LEVEL_MAP = {
  ADVANCED: PRODUCT_DIFFICULTY_LEVELS.Advanced,
  INTERMEDIATE: PRODUCT_DIFFICULTY_LEVELS.Intermediate,
  BEGINNER: PRODUCT_DIFFICULTY_LEVELS.Beginner,
} as const;

type DifficultyLevelKey = keyof typeof DIFFICULTY_LEVEL_MAP;

export const mapDifficultyLevel = (difficultyLevel?: DifficultyLevelKey | null) => {
  if (!difficultyLevel) return undefined;
  return DIFFICULTY_LEVEL_MAP[difficultyLevel];
};

type GetCollectionEntityProps = {
  productId: string;
  name: string;
  slug: string;
  imageUrl: string;
  badges: string[];
  productType: CollectionEntity['productType'];
  urlPath: string;
  reviewCount: number;
  rating: number;
  isPartOfCourseraPlus: boolean;
  isPathwayContent: boolean;
  productDifficultyLevel: CollectionEntity['productDifficultyLevel'];
  partners: { id: string; name: string; squareLogo: string }[];
  skills?: string[];
  skillsTags?: { id: string; name: string; isToolSoftware: boolean }[];
  coBrandingEnabled?: boolean;
  lastUpdated?: string;
  totalAssignmentCount?: number;
  hasTopInstructor?: boolean;
  entityIndex: number;
};

export const getCollectionEntity = ({
  productId,
  name,
  slug,
  imageUrl,
  badges,
  productType,
  urlPath,
  reviewCount,
  rating,
  isPartOfCourseraPlus,
  isPathwayContent,
  productDifficultyLevel,
  partners,
  skills,
  skillsTags,
  coBrandingEnabled,
  lastUpdated,
  totalAssignmentCount,
  hasTopInstructor,
  entityIndex,
}: GetCollectionEntityProps): CollectionEntity => {
  const entity: CollectionEntity = {
    id: productId,
    name,
    slug,
    imageUrl,
    statusTags: badges ?? [],
    productType,
    link: urlPath,
    numProductRatings: reviewCount,
    avgProductRating: rating,
    isPartOfCourseraPlus,
    isPathwayContent,
    productDifficultyLevel,
    partners:
      partners.map(({ id: partnerId, name: partnerName, squareLogo }) => ({
        id: partnerId,
        name: partnerName,
        logo: squareLogo ?? '',
      })) ?? [],
    partnerLogos: partners.map((partner) => partner.squareLogo).filter(Boolean) ?? [],
    skills,
    skillsTags,
    coBrandingEnabled,
    lastUpdated,
    totalAssignmentCount,
    hasTopInstructor,
    trackingData: {
      id: productId,
      entityId: `${productType}~${productId}`,
      entityIndex,
      name,
      slug,
    },
  };

  return entity;
};

export const convertProductCardSpecializationToCollectionEntity = (
  specialization: ProductCardSpecializationFragmentFragment,
  entityIndex: number
): CollectionEntity | null => {
  if (!('canonical' in specialization.productTypeAttributes)) return null;
  const { marketingProductType, productTypeAttributes, badges } = specialization;
  const { isPartOfCourseraPlus, isPathwayContent, rating, reviewCount } = productTypeAttributes;
  const { id, slug, name, cardImageUrl, difficultyLevel, partners, urlPath } = productTypeAttributes.canonical;

  let productType;
  if (marketingProductType === 'SPECIALIZATION') productType = 'SPECIALIZATION' as const;
  else if (marketingProductType === 'PROFESSIONAL_CERTIFICATE') productType = 'PROFESSIONAL CERTIFICATE' as const;

  return getCollectionEntity({
    productId: id,
    name,
    slug,
    imageUrl: cardImageUrl,
    badges: badges ?? [],
    productType,
    urlPath,
    reviewCount,
    rating,
    isPartOfCourseraPlus,
    isPathwayContent,
    productDifficultyLevel: mapDifficultyLevel(difficultyLevel),
    partners: partners.map((partner) => ({
      id: partner.id,
      name: partner.name,
      squareLogo: partner.squareLogo ?? '',
    })),
    entityIndex,
  });
};
